import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import moment from 'moment';

import { BaseReact } from '../../base.model';
import { ProfileQL } from '../../models/Profile';
import { CfdiIndexFilter, CfdiIndexPage, CfdiIndexStatus, CFDI_STATUS_LABEL, compactFilter, filterFromSearch, filterToSearchParams } from '../../models/CfdiIndex';
import { graphqlSchema } from '../../services/graphql.schema';
import { downloadReportCsv, ReportKind } from '../../services/fiscalpopApi';
import sharedToasterSubject from '../../services/shared.toasterSubject';
import SectionTitleBar from '../components/titlebar';
import DateRangeInput from '../Forms/DateRange';
import Select from '../Forms/Select';
import Button from '../Forms/Button';
import Checkbox from '../Forms/Checkbox';
import Loading from '../Animations/loadScreen';
import { formatCurrencyStyled } from '../visuals/currency.formatted';
import { ReceptorAutocomplete, describeFilter } from '../Facturas/Facturas.filters';
import { applyTipoFilter, entryTypeLabel, TIPO_FILTER_OPTIONS, tipoFilterValueFrom, TipoFilterValue } from '../Facturas/facturas.actions';

import './FacturasReportes.scss';

interface FacturasReportesProps extends BaseReact {
    currentUser: ProfileQL;
}

const PREVIEW_SIZE = 10;

const STATUS_OPTIONS: { value: string; label: string }[] = [
    { value: 'vigente', label: 'Solo vigentes' },
    { value: '', label: 'Todos los estados' },
    { value: 'cancelada', label: 'Solo canceladas' },
    { value: 'sustituida', label: 'Solo sustituidas' },
];

const defaultRange = (): Pick<CfdiIndexFilter, 'start' | 'end'> => ({
    start: moment().startOf('month').valueOf(),
    end: moment().endOf('month').valueOf(),
});

function FacturasReportes({ currentUser, history, location }: FacturasReportesProps) {
    const initial = useMemo(() => {
        const { filter } = filterFromSearch(location.search);
        // Reports default to the current month and to vigentes only when the list view did not hand over a filter
        const base: CfdiIndexFilter = { ...filter };
        if (base.start === undefined && base.end === undefined) Object.assign(base, defaultRange());
        if (base.status === undefined && !Object.keys(filter).length) base.status = 'vigente';
        return compactFilter(base);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [filter, setFilter] = useState<CfdiIndexFilter>(initial);
    const [downloading, setDownloading] = useState<ReportKind | ''>('');

    useEffect(() => {
        const search = filterToSearchParams(filter);
        if (search !== location.search) history.replace({ pathname: location.pathname, search });
    }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const { data, loading, error } = useQuery<{ getCfdiIndexPage: CfdiIndexPage }>(graphqlSchema.FISCALPOP.CFDI.getCfdiIndexPage, {
        variables: { filter, page: 1, pageSize: PREVIEW_SIZE },
        fetchPolicy: 'cache-and-network',
    });
    const preview = data?.getCfdiIndexPage;
    const total = preview?.total || 0;

    useEffect(() => {
        if (error) sharedToasterSubject.next({ type: 'error', message: `Error al consultar CFDIs: ${error.graphQLErrors?.[0]?.message || error.message}` });
    }, [error]);

    const update = (next: CfdiIndexFilter) => setFilter(compactFilter(next));

    const handleRange = (start: Date, end: Date) => {
        const next: CfdiIndexFilter = { ...filter };
        if (start) next.start = moment(start).startOf('day').valueOf(); else delete next.start;
        if (end) next.end = moment(end).endOf('day').valueOf(); else delete next.end;
        update(next);
    };
    const handleReceptor = (rfc: string | null) => {
        const rest: CfdiIndexFilter = { ...filter };
        delete rest.rfc;
        update(rfc ? { ...rest, rfc } : rest);
    };
    const handleStatus = (value: string) => {
        const rest: CfdiIndexFilter = { ...filter };
        delete rest.status;
        update(value ? { ...rest, status: value as CfdiIndexStatus } : rest);
    };
    const handleTipo = (value: TipoFilterValue) => update(applyTipoFilter(filter, value));
    const toggleIncludeAll = (include: boolean) => handleStatus(include ? '' : 'vigente');

    const download = (kind: ReportKind) => async () => {
        if (downloading || !total) return;
        setDownloading(kind);
        sharedToasterSubject.next({ type: 'warning', message: 'Generando reporte…' });
        try {
            const filename = await downloadReportCsv(kind, filter);
            sharedToasterSubject.next({ type: 'confirm', message: `Reporte descargado: ${filename}` });
        } catch (e) {
            console.error('Report download failed: ', e);
            sharedToasterSubject.next({ type: 'error', message: (e as Error).message || 'No fue posible generar el reporte' });
        } finally {
            setDownloading('');
        }
    };

    const chips = describeFilter(filter);
    const hasRange = filter.start !== undefined || filter.end !== undefined;

    return (
        <div id="FacturasReportes">
            <SectionTitleBar currentUser={currentUser} title="Reportes de facturas" />
            <div className="reportesContent">
                <div className="card table">
                    <div className="cardTitle">
                        <h4>Filtros del reporte</h4>
                    </div>
                    <div className="cardBody">
                        <div className="filtersRow">
                            <DateRangeInput label="Periodo (inicio / fin)" startDate={filter.start !== undefined ? new Date(filter.start) : null} endDate={filter.end !== undefined ? new Date(filter.end) : null}
                                maxDate={moment().endOf('day').toDate()} onChange={handleRange} />
                            <ReceptorAutocomplete rfc={filter.rfc} onChange={handleReceptor} />
                            <Select label="Tipo de comprobante" options={TIPO_FILTER_OPTIONS} value={tipoFilterValueFrom(filter)} onChange={handleTipo} />
                            <Select label="Estado" options={STATUS_OPTIONS} value={filter.status || ''} onChange={handleStatus} />
                        </div>
                        <div className="filtersRow secondary">
                            <Checkbox label="Incluir canceladas y sustituidas en el reporte" value={!filter.status} onChange={toggleIncludeAll} />
                            <div className="chips">
                                {chips.map(chip => <span className="chip" key={chip}>{chip}</span>)}
                            </div>
                        </div>
                        {
                            !hasRange ?
                                <p className="_warning">Sin un periodo el reporte incluye todo el historial; los reportes muy grandes (más de 50,000 CFDIs) se rechazan.</p>
                                : null
                        }
                    </div>
                </div>

                <div className="card table">
                    <div className="cardTitle _withActions">
                        <h4>
                            {loading && !preview ? 'Consultando…' : `${total.toLocaleString('es-MX')} CFDI${total === 1 ? '' : 's'} coinciden`}
                        </h4>
                        <div className="_actions">
                            <Button primary={true} size="small" handleClick={download('facturas')} disabled={!total || !!downloading}>
                                <span>{downloading === 'facturas' ? 'Generando…' : 'Reporte de facturas (CSV)'}</span>
                            </Button>
                            <Button secondary={true} size="small" handleClick={download('conceptos')} disabled={!total || !!downloading}>
                                <span>{downloading === 'conceptos' ? 'Generando…' : 'Reporte de conceptos (CSV)'}</span>
                            </Button>
                        </div>
                    </div>
                    <div className="cardBody">
                        <Loading display={loading && !preview} relativePos={true} />
                        <p className="_hint">
                            El <b>reporte de facturas</b> incluye una fila por CFDI con importes, impuestos y estado.
                            El <b>reporte de conceptos</b> incluye una fila por producto o servicio facturado (y por documento relacionado en complementos de pago).
                        </p>
                        {
                            preview?.items?.length ?
                                <div className="_preview">
                                    <p className="_previewTitle">Vista previa (primeros {Math.min(PREVIEW_SIZE, total)} de {total.toLocaleString('es-MX')})</p>
                                    <div className="_previewTable">
                                        <div className="_row _head">
                                            <p>Fecha</p><p>Tipo</p><p>Serie-Folio</p><p>Receptor</p><p>Estado</p><p className="end">Total</p>
                                        </div>
                                        {
                                            preview.items.map(entry => (
                                                <div className="_row" key={entry.uuid}>
                                                    <p>{moment(entry.created).format('DD MMM YYYY')}</p>
                                                    <p>{entryTypeLabel(entry)}</p>
                                                    <p>{entry.serie || '—'}-{entry.folio || '—'}</p>
                                                    <p className="_receptor" title={entry.receptorRfc}>{entry.receptorNombre}</p>
                                                    <p className={`_st _${entry.status}`}>{CFDI_STATUS_LABEL[entry.status]}</p>
                                                    <p className="end">{formatCurrencyStyled(entry.total || 0)}</p>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                                : (!loading ? <p className="_empty">Ningún CFDI coincide con los filtros seleccionados.</p> : null)
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FacturasReportes;
